"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useSettingsStore } from "@/lib/settings-store"
import { useTagSetStore } from "@/lib/tag-set-store"
import { useVRStore } from "@/lib/vr-store"
import {
  MAX_MOUSE_DRAG_DURATION_MS,
  MIN_HORIZONTAL_RATIO,
  WHEEL_COOLDOWN_MS,
  accumulateWheel,
  nextStepTarget,
  startsInBottomZone,
  stepSubtitle,
  swipeDirection,
  tagLabel,
  type StepDirection,
} from "@/lib/tag-step"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

/**
 * Next / previous tag gesture (see lib/tag-step.ts for the rules): swipe sideways near the bottom of the screen to step through
 * the tag sets. Touch swipe on phones, a mouse drag or a two-finger sideways trackpad swipe on desktop. A small label shows
 * where it landed and fades after about a second.
 *
 * Costs nothing while idle: it re-renders only for its own label, and reads the tag set store when a gesture fires, not on every
 * change (CLAUDE.md "High-frequency state").
 */

const LABEL_MS = 1100

/** Things a swipe must never start on: controls, dialogs, media with its own controls, and anything that scrolls sideways itself. */
const IGNORE_SELECTOR =
  'input, textarea, select, button, a[href], [role="dialog"], [role="slider"], [role="menu"], [data-no-tag-swipe], video[controls], audio[controls], canvas'

function startsOnIgnoredElement(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true
  if (target.closest(IGNORE_SELECTOR)) return true
  for (let el: Element | null = target; el && el !== document.body; el = el.parentElement) {
    if (el.scrollWidth > el.clientWidth + 2) {
      const overflowX = getComputedStyle(el).overflowX
      if (overflowX === "auto" || overflowX === "scroll") return true // a tag strip or carousel: its own swipe
    }
  }
  return false
}

function blocked(): boolean {
  if (typeof document === "undefined") return true
  if (useVRStore.getState().isVRMode) return true
  if (document.querySelector('[role="dialog"][data-state="open"]')) return true // a post viewer or menu is open
  return false
}

interface Start {
  x: number
  y: number
  t: number
}

export function TagStepGesture() {
  const enabled = useSettingsStore((state) => state.tagStepGesture)
  const t = useTranslations("settingsPanel")
  const noOthersText = t("tagStepNoOthers")
  const [label, setLabel] = useState<{ text: string; position: string; key: number } | null>(null)
  const [visible, setVisible] = useState(false)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const counter = useRef(0)

  const showLabel = useCallback((text: string, position: string) => {
    counter.current += 1
    setLabel({ text, position, key: counter.current })
    setVisible(true)
    if (hideTimer.current) clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setVisible(false), LABEL_MS)
  }, [])

  const step = useCallback((direction: StepDirection) => {
    const store = useTagSetStore.getState()
    const end = useSettingsStore.getState().tagStepEnd
    const target = nextStepTarget(
      { sets: store.sets, groups: store.groups, activeGroupId: store.activeGroupId, activeSetId: store.activeSetId, focus: store.focus },
      direction,
      end === "set" ? "set" : "continuous", // older saved values ("loop", "nextGroup", "random") mean the default
    )
    if (!target) {
      showLabel(noOthersText, "")
      return
    }
    store.focusTag(target.setId, target.focusTag)
    showLabel(tagLabel(target.tag), stepSubtitle(target))
  }, [showLabel, noOthersText])

  useEffect(() => {
    if (!enabled) return
    let start: Start | null = null
    let wheelTotal = 0
    let wheelLockedUntil = 0
    let swallowClickUntil = 0

    const begin = (x: number, y: number, target: EventTarget | null) => {
      start = null
      if (!startsInBottomZone(y, window.innerHeight) || startsOnIgnoredElement(target) || blocked()) return
      start = { x, y, t: performance.now() }
    }
    const finish = (x: number, y: number, maxDurationMs?: number): StepDirection | null => {
      if (!start) return null
      const direction = swipeDirection({ startX: start.x, startY: start.y, endX: x, endY: y, durationMs: performance.now() - start.t }, maxDurationMs)
      start = null
      return direction
    }

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        start = null
        return
      }
      begin(e.touches[0].clientX, e.touches[0].clientY, e.target)
    }
    const onTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0]
      if (!touch) return
      const direction = finish(touch.clientX, touch.clientY)
      if (direction) step(direction)
    }
    const onTouchCancel = () => {
      start = null
    }

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 || e.detail > 1) return
      begin(e.clientX, e.clientY, e.target)
    }
    const onMouseUp = (e: MouseEvent) => {
      const direction = finish(e.clientX, e.clientY, MAX_MOUSE_DRAG_DURATION_MS)
      if (!direction) return
      step(direction)
      window.getSelection()?.removeAllRanges() // dragging with a mouse selects whatever it passed over
      swallowClickUntil = performance.now() + 80 // the drag must not also click the post under the pointer
    }
    // A mouse drag that starts on a picture would start the browser's own "drag this image" and swallow the mouse-up, so the swipe
    // would never finish. While a swipe is being tracked, cancel that native drag (the mouse events then keep coming).
    const onDragStart = (e: DragEvent) => {
      if (start) e.preventDefault()
    }
    const onClickCapture = (e: MouseEvent) => {
      if (performance.now() < swallowClickUntil) {
        e.stopPropagation()
        e.preventDefault()
      }
    }

    const onWheel = (e: WheelEvent) => {
      if (!startsInBottomZone(e.clientY, window.innerHeight)) return
      const horizontal = Math.abs(e.deltaX) >= Math.abs(e.deltaY) * MIN_HORIZONTAL_RATIO && Math.abs(e.deltaX) > 0
      if (!horizontal) {
        wheelTotal = 0
        return
      }
      if (startsOnIgnoredElement(e.target) || blocked()) return
      e.preventDefault() // keeps a sideways trackpad swipe from also going Back in the browser
      const now = performance.now()
      if (now < wheelLockedUntil) return
      const result = accumulateWheel(wheelTotal, e.deltaX, e.deltaY)
      wheelTotal = result.total
      if (result.direction) {
        wheelLockedUntil = now + WHEEL_COOLDOWN_MS
        step(result.direction)
      }
    }

    document.addEventListener("touchstart", onTouchStart, { passive: true })
    document.addEventListener("touchend", onTouchEnd, { passive: true })
    document.addEventListener("touchcancel", onTouchCancel, { passive: true })
    document.addEventListener("mousedown", onMouseDown)
    document.addEventListener("mouseup", onMouseUp)
    document.addEventListener("click", onClickCapture, true)
    document.addEventListener("dragstart", onDragStart)
    // A non-passive wheel listener on the whole page makes every scroll wait for it (laggy scrolling), so it is attached only
    // while the pointer is inside the bottom strip, where a sideways swipe could happen.
    let wheelAttached = false
    const setWheel = (attach: boolean) => {
      if (attach === wheelAttached) return
      wheelAttached = attach
      if (attach) document.addEventListener("wheel", onWheel, { passive: false })
      else document.removeEventListener("wheel", onWheel)
    }
    const onPointerMove = (e: MouseEvent) => setWheel(startsInBottomZone(e.clientY, window.innerHeight))
    document.addEventListener("mousemove", onPointerMove, { passive: true })
    return () => {
      setWheel(false)
      document.removeEventListener("mousemove", onPointerMove)
      document.removeEventListener("touchstart", onTouchStart)
      document.removeEventListener("touchend", onTouchEnd)
      document.removeEventListener("touchcancel", onTouchCancel)
      document.removeEventListener("mousedown", onMouseDown)
      document.removeEventListener("mouseup", onMouseUp)
      document.removeEventListener("click", onClickCapture, true)
      document.removeEventListener("dragstart", onDragStart)
    }
  }, [enabled, step])

  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current)
  }, [])

  if (!enabled || !label) return null
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="tag-step-label"
      className={cn(
        "pointer-events-none fixed left-1/2 z-[60] -translate-x-1/2 rounded-full border border-border/50 bg-background/85 px-4 py-1.5 shadow-lg backdrop-blur-md transition-opacity duration-300",
        visible ? "opacity-100" : "opacity-0",
      )}
      style={{ bottom: "calc(6.5rem + env(safe-area-inset-bottom, 0px) + var(--sib-player-height, 0px))" }}
    >
      <span className="text-sm font-semibold">{label.text}</span>
      {label.position ? <span className="ml-2 text-xs tabular-nums text-muted-foreground">{label.position}</span> : null}
    </div>
  )
}
